import React, { useState, useEffect, useRef } from "react";
import { getPercentileColor } from "../../utils/colorUtils";
import { fetchAPI } from "../../config/api";

const Bar = ({ percentile }) => {
  const width = percentile ? Math.min(percentile, 100) : 0;
  const color = percentile ? getPercentileColor(percentile) : "#d1d5db";
  
  return (
    <div className="relative w-full h-3 bg-gray-200 rounded">
      <div
        className="absolute left-0 top-0 h-3 rounded transition-all duration-500 ease-out"
        style={{
          width: `${width}%`,
          backgroundColor: color,
        }}
      />
    </div>
  );
};

const StatRow = ({ label, value, percentile, rank, totalTeams, decimals = 1, suffix = "" }) => {
  const displayValue = value != null ? Number(value).toFixed(decimals) : "-";
  const percentileText = percentile != null ? `${percentile}th percentile` : "-";
  const rankingText = rank && totalTeams ? `#${rank} of ${totalTeams}` : "-";
  
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between items-baseline text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-mono text-sm text-gray-900">
          {displayValue}{suffix}
        </span>
      </div>
      <Bar percentile={percentile} />
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-500">{percentileText}</span>
        <span className="font-mono text-gray-500">{rankingText}</span>
      </div>
    </div>
  );
};

const Category = ({ title, stats }) => (
  <div className="flex flex-col gap-3 border border-gray-200 rounded-xl p-3 bg-white shadow-sm">
    <h3 className="text-xs font-semibold text-gray-600 tracking-wide uppercase">
      {title}
    </h3>
    <div className="flex flex-col gap-3">
      {stats.map((stat) => (
        <StatRow key={stat.label} {...stat} />
      ))}
    </div>
  </div>
);

export const TeamPercentiles = ({ orgId, years }) => {
  const [percentiles, setPercentiles] = useState(null);
  const [selectedYear, setSelectedYear] = useState(years?.[0] ?? null);
  const [activeTab, setActiveTab] = useState("batting");
  const [isLoading, setIsLoading] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const skipNextEffectRef = useRef(false);
  const selectedYearRef = useRef(selectedYear);

  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  useEffect(() => {
    if (years?.length > 0) {
      const currentYear = selectedYearRef.current;
      if (!currentYear) {
        skipNextEffectRef.current = true;
        setSelectedYear(years[0]);
      } else if (!years.includes(currentYear)) {
        skipNextEffectRef.current = true;
        setSelectedYear(years[0]);
      }
    }
  }, [years]);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    if (!orgId || !selectedYear) return;

    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchAPI(`/api/team-percentiles/${orgId}/${selectedYear}`, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setPercentiles(data);
          setIsLoading(false);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Failed to fetch team percentiles:", e);
          setIsLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [orgId, selectedYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const p = activeTab === "batting" ? percentiles?.batting : percentiles?.pitching;

  const createStatConfig = (key, label, decimals = 1, suffix = "") => ({
    label,
    value: p?.stats?.[key],
    percentile: p?.stats?.[`${key}_percentile`],
    rank: p?.stats?.[`${key}_rank`],
    totalTeams: p?.team_count,
    decimals,
    suffix,
  });

  const battingCategories = [
    {
      title: "Batting",
      stats: [
        createStatConfig("woba", "wOBA", 3),
        createStatConfig("wrc_plus", "wRC+", 0),
        createStatConfig("k_pct", "K%", 1, "%"),
        createStatConfig("bb_pct", "BB%", 1, "%"),
        createStatConfig('ops_plus', 'OPS+', 0),
        createStatConfig('rc_per_pa', 'RC/PA', 1),
        createStatConfig('babip', 'BABIP', 3),
      ],
    },
    {
      title: "Value",
      stats: [
        createStatConfig("war", "WAR", 1),
        createStatConfig("sos_adj_war", "SOS-Adj WAR", 1),
        createStatConfig("wpa", "WPA", 1),
        createStatConfig("wpa_li", "WPA/LI", 1),
        createStatConfig("rea", "RE24", 1),
        createStatConfig("clutch", "Clutch", 1),
      ],
    },
  ];

  const pitchingCategories = [
    {
      title: "Pitching",
      stats: [
        createStatConfig("era", "ERA", 2),
        createStatConfig("whip", "WHIP", 2),
        createStatConfig("fip", "FIP", 2),
        createStatConfig("xfip", "xFIP", 2),
        createStatConfig("k_pct", "K%", 1, "%"),
        createStatConfig("bb_pct", "BB%", 1, "%"),
        createStatConfig("k_minus_bb_pct", "K-BB%", 1, "%"),
      ],
    },
    {
      title: "Value",
      stats: [
        createStatConfig("war", "WAR", 1),
        createStatConfig("sos_adj_war", "SOS-Adj WAR", 1),
        createStatConfig("pwpa", "WPA", 1),
        createStatConfig("pwpa_li", "WPA/LI", 1),
        createStatConfig("prea", "RE24", 1),
        createStatConfig("clutch", "Clutch", 1),
      ],
    },
  ];

  const categories = activeTab === "batting" ? battingCategories : pitchingCategories;

  return (
    <div className="p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between mb-4 border-b border-gray-100 pb-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-wider text-gray-500">
              Team Analysis
            </span>
            <h2 className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent select-none">
              Percentile Rankings
            </h2>
          </div>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab("batting")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "batting"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Batting
            </button>
            <button
              onClick={() => setActiveTab("pitching")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "pitching"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Pitching
            </button>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdown(!dropdown)}
              className="text-sm px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
            >
              {selectedYear}
              <span className="ml-1 text-gray-400">▼</span>
            </button>
            {dropdown && (
              <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow-md w-20 right-0">
                {years?.map((y) => (
                  <button
                    key={y}
                    onClick={() => {
                      setSelectedYear(y);
                      setDropdown(false);
                    }}
                    className={`block w-full text-left px-3 py-1 text-sm ${
                      y === selectedYear
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {p ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {categories.map((c) => (
            <Category key={c.title} {...c} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {isLoading ? "Loading percentiles..." : "No percentile data available for this year"}
        </div>
      )}
    </div>
  );
};
